import { Link } from "react-router-dom";
import { ArrowRight, Phone, Calendar, BookOpen, ExternalLink } from "lucide-react";
import { SITE } from "@/lib/site";

export default function Hero() {
  return (
    <section data-testid="home-hero" className="relative overflow-hidden pt-12 md:pt-20 pb-20 md:pb-24">
      {/* Tulsa skyline backdrop (Bevel-style) — image path inlined so webpack treats it as a runtime URL, not a module to bundle */}
      <div aria-hidden className="hero-skyline" style={{ backgroundImage: "url('/images/tulsa-skyline.jpg')" }} />
      {/* Cream paper texture overlay */}
      <div aria-hidden className="absolute inset-0 -z-10 paper-bg opacity-60" />
      <div aria-hidden className="absolute inset-0 -z-10" style={{ background: "radial-gradient(900px 460px at 80% -10%, rgba(26,110,152,0.10), transparent 60%), radial-gradient(700px 460px at -10% 0%, rgba(10,37,64,0.06), transparent 60%)" }} />

      <div className="container-narrow relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* LEFT: editorial column */}
          <div className="lg:col-span-7 hero-reveal">
            <div className="flex items-center gap-3 text-[12px] tracking-[0.24em] uppercase text-navy-500 font-medium">
              <span className="h-1.5 w-1.5 rounded-full bg-gold-500 animate-soft-pulse" />
              Owasso · Tulsa · Claremore · Collinsville
            </div>

            <h1 className="font-serif-display text-[44px] sm:text-[58px] lg:text-[78px] leading-[0.98] tracking-tight text-navy-800 mt-7">
              Trusted accounting<br />and tax guidance,<br />
              <span className="italic text-gold-500">since 2004.</span>
            </h1>

            <div className="mt-7 rule-gold w-40" />

            <p className="mt-7 text-navy-600 text-[17px] md:text-[18px] leading-relaxed max-w-xl">
              St. John Accounting and Tax helps small businesses, families, and individuals achieve a better understanding of their financial position through accurate, compliant, and timely accounting/tax services.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" data-testid="hero-book-cta" className="btn-primary">
                <Calendar size={17} /> Schedule a Consultation <ArrowRight size={16} />
              </a>
              <Link to="/resources" data-testid="hero-resources-cta" className="btn-secondary">
                <BookOpen size={16} /> View Resources
              </Link>
              <a href={SITE.phoneTel} data-testid="hero-call-cta" className="hidden sm:inline-flex items-center gap-2 ml-2 text-navy-600 hover:text-navy-800 text-sm font-medium transition-colors">
                <Phone size={15} className="text-gold-500" /> {SITE.phone}
              </a>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-8 max-w-xl border-t border-navy-100 pt-7">
              {[
                { k: "20+", v: "Years experience" },
                { k: "Platinum", v: "QuickBooks ProAdvisor" },
                { k: "BBB", v: "Accredited business" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="font-serif-display text-3xl text-navy-800 font-semibold">{s.k}</div>
                  <div className="text-[12px] uppercase tracking-wider text-navy-500 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: editorial / publication-style card */}
          <div className="lg:col-span-5 hero-reveal lg:pt-12">
            <div className="relative">
              <div aria-hidden className="absolute -top-3 left-0 right-0 h-px bg-gradient-to-r from-gold-500/40 via-gold-300/20 to-transparent" />
              <div className="text-[10px] tracking-[0.3em] uppercase text-gold-600 font-semibold pt-5">A Standard of Practice</div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-navy-800 mt-4 leading-[1.05]">
                A small-town firm with a big-firm standard of practice.
              </h2>
              <p className="mt-5 text-navy-600 text-[15px] leading-relaxed">
                We listen first and then tailor our recommendations to your needs and goals.
              </p>

              <ul className="mt-7 divide-y divide-navy-100 border-y border-navy-100">
                {[
                  { n: "01", t: "Business Accounting & Payroll Services", d: "Clean records, on-time payroll, audit-ready clarity." },
                  { n: "02", t: "Tax Planning & Preparation", d: "Businesses & Individuals, Federal & State." },
                  { n: "03", t: "Insurance Services", d: "Commercial & personal, through St. John Insurance.", href: "https://www.stjohninsurance.net/" },
                  { n: "04", t: "Financial Planning & Wealth Management", d: "Investments & long-term planning, through St. John Financial Group.", href: "https://www.stjohnfinancialgroup.com/" },
                ].map((item) => {
                  const isLink = !!item.href;
                  const Wrapper = isLink ? "a" : "li";
                  const wrapperProps = isLink
                    ? { href: item.href, target: "_blank", rel: "noopener noreferrer", "data-testid": `hero-service-${item.n}`, className: "grid grid-cols-[40px_1fr_auto] gap-4 py-3.5 hover:bg-navy-50/40 transition-colors -mx-1 px-1 rounded" }
                    : { className: "grid grid-cols-[40px_1fr_auto] gap-4 py-3.5", "data-testid": `hero-service-${item.n}` };
                  const Inner = (
                    <>
                      <div className="text-gold-500 font-serif-display text-xl tabular-nums">{item.n}</div>
                      <div>
                        <div className="font-medium text-navy-800 text-[14.5px]">{item.t}</div>
                        <div className="text-navy-500 text-[13px] mt-0.5">{item.d}</div>
                      </div>
                      {isLink && <ExternalLink size={14} className="text-gold-500 self-center" />}
                    </>
                  );
                  return isLink ? (
                    <li key={item.n} className="block">
                      <Wrapper {...wrapperProps}>{Inner}</Wrapper>
                    </li>
                  ) : (
                    <Wrapper key={item.n} {...wrapperProps}>{Inner}</Wrapper>
                  );
                })}
              </ul>

              <div className="mt-6 text-[12px] italic text-navy-500">
                "Living the dream is living in a small town and growing a small business focused on peace, prosperity, and professionalism."
                <div className="mt-1 not-italic text-navy-400">David St. John</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
